// A simple PDF generator that doesn't rely on autoTable
import {jsPDF} from 'jspdf'
import {format} from 'date-fns'

/**
 * Generate a simple PDF from reservation statement data without using autoTable
 *
 * @param {Object} options - PDF generation options
 * @param {Array} options.reservations - Array of reservation data
 * @param {String} options.currency - Currency code (default: 'EUR')
 * @param {String} options.title - PDF title
 * @param {String} options.period - Period string (e.g., 'January 2023')
 * @param {String} options.filename - Output filename (default: 'reservation-statement.pdf')
 * @returns {jsPDF} - The generated PDF document
 */
export const generateSimplePDF = options => {
    const {
        reservations = [],
        currency = 'EUR',
        title = 'Reservation Statement',
        period = '',
        filename = 'reservation-statement.pdf',
    } = options

    if (!reservations || !Array.isArray(reservations) || reservations.length === 0) {
        throw new Error('No reservations data provided')
    }

    // Create a new PDF document
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
    })

    // Add a white background color
    doc.setFillColor(255, 255, 255) // Pure white
    doc.rect(0, 0, doc.internal.pageSize.width, doc.internal.pageSize.height, 'F')

    // Add a header bar
    doc.setFillColor(0, 128, 128) // Teal color
    doc.rect(0, 0, doc.internal.pageSize.width, 15, 'F')

    // Define consistent left margin for all elements
    const leftMargin = 14

    // Extract supplier name from title if present
    let headerText = 'Zero Stars Hotels Reservation Statements'

    // Check if title contains supplier name (format: "Monthly Invoice - Supplier Name")
    const titleParts = title.split(' - ')
    if (titleParts.length > 1) {
        // Add "for Supplier Name" to the header text
        headerText += ` for ${titleParts[1]}`
    }

    // Add header text
    doc.setTextColor(255, 255, 255) // White text
    doc.setFontSize(16)
    doc.text(headerText, leftMargin, 10)

    // Reset text color to black for the rest of the document
    doc.setTextColor(0, 0, 0)

    // Add period if provided
    if (period) {
        doc.setFontSize(12)
        doc.text(`Period: ${period}`, leftMargin, 25)
    }

    // Add generation date
    const currentDate = format(new Date(), 'yyyy-MM-dd HH:mm')
    doc.setFontSize(10)
    doc.text(`Generated: ${currentDate}`, leftMargin, 32)

    // Format currency for display
    const formatCurrency = value => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(value)
    }

    // Format date for display
    const formatDate = date => {
        if (!date) return ''
        return format(new Date(date), 'yyyy-MM-dd')
    }

    // Format sustainability level for display
    const formatSustainabilityLevel = level => {
        if (!level) return 'Basic'
        switch (level.toUpperCase()) {
            case 'ZERO':
                return 'Zero'
            case 'SUSTAINABLE':
                return 'Sustainable'
            case 'BASIC':
            default:
                return 'Basic'
        }
    }

    // Calculate totals
    const totalOriginalAmount = reservations.reduce((sum, item) => sum + item.originalAmount, 0)
    const totalFinalAmount = reservations.reduce((sum, item) => sum + item.finalAmount, 0)
    const totalPaymentCharge = reservations.reduce((sum, item) => sum + item.paymentCharge, 0)
    const totalCommissionAmount = reservations.reduce((sum, item) => sum + item.commissionAmount, 0)
    // Draw a simple table manually
    let y = 45
    const lineHeight = 10
    // Calculate available width for the table (same as footer line)
    const pageWidth = doc.internal.pageSize.width
    const tableStartX = leftMargin - 4 // Match footer line start
    const tableEndX = pageWidth - leftMargin + 4 // Match footer line end
    const availableWidth = tableEndX - tableStartX

    // Distribute column widths proportionally across the full available width
    // Book Number, Guest Name, Room Type, Check In/Out, Nights, Comm %, Result, Original, Final, Payment, Commission
    const colWidthRatios = [0.08, 0.12, 0.1, 0.12, 0.06, 0.07, 0.12, 0.08, 0.08, 0.08, 0.09] // Proportional widths
    const colWidths = colWidthRatios.map(ratio => availableWidth * ratio)
    const startX = tableStartX

    // Calculate column positions
    const colPos = []
    let currentX = startX
    for (let i = 0; i < colWidths.length; i++) {
        colPos.push(currentX)
        currentX += colWidths[i]
    }

    // Define rows per page and track current page
    const rowsPerPage = 7 // Limit to 7 rows per page
    let currentPage = 1
    let totalPages = Math.ceil(reservations.length / rowsPerPage)

    // Function to draw table header on each page
    const drawTableHeader = pageY => {
        // Draw table header background - use full available width
        doc.setFillColor(0, 128, 128) // Teal color for header
        doc.rect(startX, pageY - 8, availableWidth, 12, 'F')

        // Headers
        doc.setTextColor(255, 255, 255) // White text for headers
        doc.setFontSize(7) // Even smaller font size for headers
        doc.setFont(undefined, 'bold')

        // Left-aligned headers
        doc.text('Book Number', colPos[0], pageY)
        doc.text('Guest Name', colPos[1], pageY)
        doc.text('Room Type', colPos[2], pageY)
        doc.text('Check In/Out', colPos[3], pageY)

        // Right-aligned numeric headers
        const nightsHeader = 'Nights'
        const commHeader = 'Comm %'
        doc.text(nightsHeader, colPos[4] + colWidths[4] - 2 - doc.getTextWidth(nightsHeader), pageY)
        doc.text(commHeader, colPos[5] + colWidths[5] - 2 - doc.getTextWidth(commHeader), pageY)

        doc.text('Result', colPos[6] + 2, pageY)

        // Right-aligned headers for monetary columns
        const originalAmountHeader = 'Original'
        const finalAmountHeader = 'Final'
        const paymentChargeHeader = 'Payment'
        const commissionHeader = 'Commission'

        doc.text(originalAmountHeader, colPos[7] + colWidths[7] - 2 - doc.getTextWidth(originalAmountHeader), pageY)
        doc.text(finalAmountHeader, colPos[8] + colWidths[8] - 2 - doc.getTextWidth(finalAmountHeader), pageY)
        doc.text(paymentChargeHeader, colPos[9] + colWidths[9] - 2 - doc.getTextWidth(paymentChargeHeader), pageY)
        doc.text(commissionHeader, colPos[10] + colWidths[10] - 2 - doc.getTextWidth(commissionHeader), pageY)

        // Reset text color to black for data
        doc.setTextColor(0, 0, 0)

        // Reset font to normal for data rows
        doc.setFontSize(7)
        doc.setFont(undefined, 'normal')

        // Draw horizontal line below headers - use full available width
        doc.setDrawColor(0, 128, 128) // Teal color for lines
        doc.setLineWidth(0.5)
        doc.line(startX, pageY + 5, startX + availableWidth, pageY + 5)

        return pageY + lineHeight + 5 // Return the new Y position
    }

    // Function to add a new page
    const addNewPage = () => {
        doc.addPage()
        currentPage++

        // Add a white background color
        doc.setFillColor(255, 255, 255) // Pure white
        doc.rect(0, 0, doc.internal.pageSize.width, doc.internal.pageSize.height, 'F')

        // Add a header bar
        doc.setFillColor(0, 128, 128) // Teal color
        doc.rect(0, 0, doc.internal.pageSize.width, 15, 'F')

        // Use the same header text with supplier name if present
        doc.setTextColor(255, 255, 255) // White text
        doc.setFontSize(16)
        doc.setFont(undefined, 'normal') // Reset font style to normal
        doc.text(headerText, leftMargin, 10)

        // Reset text color to black
        doc.setTextColor(0, 0, 0)

        // Add period if provided
        if (period) {
            doc.setFontSize(12)
            doc.text(`Period: ${period}`, leftMargin, 25)
        }

        // Reset font settings for table data
        doc.setFontSize(7) // Match the header font size
        doc.setFont(undefined, 'normal') // Ensure font is normal, not bold

        // Return to starting Y position for the table
        return 45
    }

    // Draw the initial table header
    y = drawTableHeader(y)

    // Data rows
    doc.setFontSize(7) // Match the header font size
    doc.setFont(undefined, 'normal')

    let rowsOnCurrentPage = 0

    reservations.forEach(item => {
        // Check if we need to start a new page
        if (rowsOnCurrentPage >= rowsPerPage) {
            y = addNewPage()
            y = drawTableHeader(y)
            rowsOnCurrentPage = 0
        }

        // Ensure font is set to normal for each row
        doc.setFontSize(7)
        doc.setFont(undefined, 'normal')

        // Use consistent white background for all rows - use full available width
        doc.setFillColor(255, 255, 255) // Pure white for all data rows
        // Position the background rectangle to align with text
        doc.rect(startX, y - 4, availableWidth, lineHeight + 8, 'F')
        rowsOnCurrentPage++

        // Don't truncate text to prevent overlapping - use full values
        const bookNumber = item.bookNumber.toString()
        const guestName = item.guestName
        console.error(
            'PDF Generator - Item sustainabilityLevel:',
            item.sustainabilityLevel,
            'for booking:',
            item.bookNumber
        )
        const roomType = formatSustainabilityLevel(item.sustainabilityLevel)
        const result = item.result

        // Reduce horizontal padding by using smaller margins
        const cellPadding = 1 // Reduced from 2

        doc.text(bookNumber, colPos[0], y)
        doc.text(guestName, colPos[1], y)
        doc.text(roomType, colPos[2], y)
        doc.text(`${formatDate(item.checkIn)}`, colPos[3], y - 2) // Adjusted position
        doc.text(`${formatDate(item.checkOut)}`, colPos[3], y + 4) // Reduced spacing between dates

        // Right-align numeric values
        const nightsText = item.nights.toString()
        const commText = `${item.commissionPercentage}%`
        doc.text(nightsText, colPos[4] + colWidths[4] - cellPadding - doc.getTextWidth(nightsText), y)
        doc.text(commText, colPos[5] + colWidths[5] - cellPadding - doc.getTextWidth(commText), y)

        // Add a bit more left padding to the Result column for better separation
        doc.text(result, colPos[6] + 2, y)

        // Right-align monetary values
        const originalAmountText = formatCurrency(item.originalAmount)
        const finalAmountText = formatCurrency(item.finalAmount)
        const paymentChargeText = formatCurrency(item.paymentCharge)
        const commissionAmountText = formatCurrency(item.commissionAmount)

        // Adjust the position for right alignment based on column width with reduced padding
        doc.text(originalAmountText, colPos[7] + colWidths[7] - cellPadding - doc.getTextWidth(originalAmountText), y)
        doc.text(finalAmountText, colPos[8] + colWidths[8] - cellPadding - doc.getTextWidth(finalAmountText), y)
        doc.text(paymentChargeText, colPos[9] + colWidths[9] - cellPadding - doc.getTextWidth(paymentChargeText), y)
        doc.text(
            commissionAmountText,
            colPos[10] + colWidths[10] - cellPadding - doc.getTextWidth(commissionAmountText),
            y
        )

        // Draw light horizontal line after each row - use full available width
        doc.setDrawColor(220, 220, 220)
        doc.setLineWidth(0.1)
        // Adjust the vertical position of the horizontal line to match the adjusted background
        doc.line(startX, y + 6, startX + availableWidth, y + 6)

        y += lineHeight + 10 // Increased row spacing
    })

    // Check if we need to add a new page for the total row
    if (rowsOnCurrentPage >= rowsPerPage) {
        y = addNewPage()
        rowsOnCurrentPage = 0

        // Ensure font is set to normal before drawing the total row
        doc.setFontSize(7)
        doc.setFont(undefined, 'normal')
    }

    // Add some space before the total row
    y += 2

    // Draw total row background - use full available width
    doc.setFillColor(230, 240, 240) // Light teal for total row
    // Adjust the vertical position of the total row background to match other rows
    doc.rect(startX, y - 4, availableWidth, lineHeight + 4, 'F')

    // Draw border around total row - use full available width
    doc.setDrawColor(0, 128, 128)
    doc.setLineWidth(0.5)
    // Adjust the border to match the background
    doc.rect(startX, y - 4, availableWidth, lineHeight + 4, 'S')

    doc.setFontSize(8) // Slightly larger font for totals but still small enough to fit
    doc.setFont(undefined, 'bold')
    doc.text('Total:', colPos[6] + 2, y)

    // Right-align total monetary values
    const totalOriginalText = formatCurrency(totalOriginalAmount)
    const totalFinalText = formatCurrency(totalFinalAmount)
    const totalPaymentText = formatCurrency(totalPaymentCharge)
    const totalCommissionText = formatCurrency(totalCommissionAmount)

    // Reduce horizontal padding for totals too
    const totalCellPadding = 1

    // Adjust the position for right alignment based on column width
    doc.text(totalOriginalText, colPos[7] + colWidths[7] - totalCellPadding - doc.getTextWidth(totalOriginalText), y)
    doc.text(totalFinalText, colPos[8] + colWidths[8] - totalCellPadding - doc.getTextWidth(totalFinalText), y)
    doc.text(totalPaymentText, colPos[9] + colWidths[9] - totalCellPadding - doc.getTextWidth(totalPaymentText), y)
    doc.text(
        totalCommissionText,
        colPos[10] + colWidths[10] - totalCellPadding - doc.getTextWidth(totalCommissionText),
        y
    )

    // Function to add footer to each page
    const addFooter = pageNum => {
        const pageWidth = doc.internal.pageSize.width
        const pageHeight = doc.internal.pageSize.height

        // Footer line - use same coordinates as table
        doc.setDrawColor(0, 128, 128)
        doc.setLineWidth(0.5)
        doc.line(tableStartX, pageHeight - 15, tableEndX, pageHeight - 15)

        // Footer text - use same format as header but without "for" to save space
        doc.setFontSize(8)
        doc.setTextColor(100, 100, 100) // Dark gray text

        // Create footer text with supplier name if present
        let footerText = 'Zero Stars Hotels - Reservation Statements'
        const titleParts = title.split(' - ')
        if (titleParts.length > 1) {
            footerText += ` - ${titleParts[1]}`
        }

        doc.text(footerText, leftMargin, pageHeight - 10)

        // Page number
        const pageInfo = `Page ${pageNum} of ${totalPages}`
        doc.text(pageInfo, pageWidth - leftMargin - doc.getTextWidth(pageInfo), pageHeight - 10)
    }

    // Add footer to the first page
    addFooter(currentPage)

    // Go through all pages to add footers
    for (let i = 1; i < currentPage; i++) {
        doc.setPage(i)
        addFooter(i)
    }

    // Save the PDF
    if (filename) {
        doc.save(filename)
    }

    return doc
}
